import type { FileEntryDomainContractDto } from "../types/FileEntry";

export interface AnswerRecord {
  id: string;
  path: string;
  value: any;
  binding: any;
  data: any;
  type?: string;
  answerType?: string;
  profileId?: string;
}

export interface AnswersFetchOptions {
  force?: boolean;
}

export interface AnswersDriver {
  fetchAnswers(userId: string, options?: AnswersFetchOptions): Promise<AnswerRecord[]>;
  fetchAnswer(
    userId: string,
    blockId: string,
    path?: string,
    options?: AnswersFetchOptions
  ): Promise<AnswerRecord | null>;
  saveAnswer(
    userId: string,
    blockId: string,
    payload: any,
    isUpdate: boolean
  ): Promise<void>;
  deleteAnswer(userId: string, blockId: string, path?: string): Promise<void>;
}

let requestQueue: Promise<void> = Promise.resolve();

/**
 * Ensures that asynchronous operations are executed sequentially.
 * Each operation is added to a shared promise chain (`requestQueue`),
 * so that it runs only after the previous operation completes.
 * 
 * @param op - A function that returns a promise representing the operation to enqueue.
 * @returns A promise that resolves with the result of the enqueued operation.
 */
function enqueue<T>(op: () => Promise<T>): Promise<T> {
  const run = requestQueue.then(op);
  requestQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

export function useAnswersDriver() {
  const MIME = "application/vnd.pivotalgroup.data.assessment.answer+json";
  const { site } = useSite();
  const { execute: executeReliableWrite } = useCandidateWriteReliability();

  const getHeaders = () => ({ "x-site-id": site.value?.id || "" });

  async function getEntry(
    path: string
  ): Promise<FileEntryDomainContractDto | null> {
    try {
      return await useApiFetch<FileEntryDomainContractDto>(`/v1/file/${path}`, {
        headers: getHeaders(),
      });
    } catch {
      return null;
    }
  }

  async function downloadEntry(
    entry: FileEntryDomainContractDto
  ): Promise<any | null> {
    const versionId = entry.versions[0]?.id;
    if (!versionId) return null;
    const raw = await useApiFetch<string>(
      `/v1/file/${site.value?.id}/download/${entry.path}?version=${versionId}`,
      { responseType: "text" }
    );
    return JSON.parse(raw);
  }

  const cache = useState<Record<string, any[] | null>>(
    "answers-file-cache",
    () => ({})
  );
  const cacheAttempted = useState<Record<string, boolean>>(
    "answers-file-cache-attempted",
    () => ({})
  );

  async function loadCache(
    userId: string,
    options: AnswersFetchOptions = { force: false }
  ): Promise<AnswerRecord[] | null> {
    if (cacheAttempted.value[userId] && !options.force) {
      return cache.value[userId] || null;
    }

    cacheAttempted.value[userId] = true;

    const entry = await getEntry(`data/${userId}/cache/answers`);
    if (!entry) {
      cache.value[userId] = null;
      return null;
    }

    const parsed = await downloadEntry(entry);
    if (Array.isArray(parsed)) {
      cache.value[userId] = parsed;
    } else if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray(parsed.answers)
    ) {
      cache.value[userId] = parsed.answers;
    } else if (parsed === null) {
      cache.value[userId] = null;
    } else {
      throw new Error(
        `Answer cache for user ${userId} must be an array or contain an answers array.`
      );
    }
    return cache.value[userId];
  }

  async function fetchAll(
    userId: string,
    options: AnswersFetchOptions = { force: false }
  ): Promise<AnswerRecord[]> {
    if (!cacheAttempted.value[userId] || options.force) {
      await loadCache(userId, options);
    }

    return cache.value[userId] || [];
  }

  async function fetchSingle(
    userId: string,
    blockId: string,
    path?: string,
    options: AnswersFetchOptions = { force: false }
  ): Promise<AnswerRecord | null> {
    const all = await fetchAll(userId, options);
    return all.find((answer) =>
      answer.id === blockId && (path === undefined || answer.path === path)
    ) || null;
  }

  async function save(
    userId: string,
    blockId: string,
    payload: any
  ): Promise<void> {
    const parentPath = `data/${userId}/assessments/${payload.path}`.replace(
      /\/+$/,
      ""
    );
    const body = { ...payload };
    delete body.blockId;
    delete body.path;
    await enqueue(() =>
      executeReliableWrite(
        {
          operation: "assessment-answer",
          targetPath: `${parentPath}/${blockId}`,
          blockId,
        },
        () =>
          useApiFetch(`/v1/file/${parentPath}`, {
            method: "POST",
            headers: {
              ...getHeaders(),
              "Content-Disposition": `form-data; name="answer"; filename="${blockId}"`,
              "Content-Type": MIME,
              "x-mkdir": true,
            },
            body: JSON.stringify(body),
          }),
      )
    );
  }

  async function remove(userId: string, blockId: string, path?: string): Promise<void> {
    const record = await fetchSingle(userId, blockId, path);
    if (!record) return;
    const url = `/v1/file/data/${userId}/assessments/${record.path}/${record.id}`;
    await enqueue(() =>
      useApiFetch(url, { method: "DELETE", headers: getHeaders() })
    );
  }

  /**
   * Ephemeral driver that stores values only in a Nuxt state. All data is
   * cleared whenever the browser is refreshed or closed.
   */
  const ephemeralCache = useState<Record<string, AnswerRecord[]>>(
    "answersDriverEphemeral",
    () => ({})
  );

  const epFetchAll = async (
    userId: string,
    options: AnswersFetchOptions = { force: false }
  ): Promise<AnswerRecord[]> => {
    return ephemeralCache.value[userId] || [];
  };

  const epFetchSingle = async (
    userId: string,
    blockId: string,
    path?: string,
    options: AnswersFetchOptions = { force: false }
  ): Promise<AnswerRecord | null> => {
    const all = await epFetchAll(userId, options);
    return all.find((answer) =>
      answer.id === blockId && (path === undefined || answer.path === path)
    ) || null;
  };

  const epSave = async (
    userId: string,
    blockId: string,
    payload: any
  ): Promise<void> => {
    if (!ephemeralCache.value[userId]) {
      ephemeralCache.value[userId] = [];
    }
    const store = ephemeralCache.value[userId];
    const idx = store.findIndex((answer) =>
      answer.id === blockId && answer.path === payload.path
    );
    if (idx !== -1) {
      store[idx] = payload;
    } else {
      store.push(payload);
    }
  };

  const epRemove = async (userId: string, blockId: string, path?: string): Promise<void> => {
    const store = ephemeralCache.value[userId];
    if (!store) return;
    const idx = store.findIndex((answer) =>
      answer.id === blockId && (path === undefined || answer.path === path)
    );
    if (idx !== -1) store.splice(idx, 1);
  };

  const ephemeralDriver: AnswersDriver = {
    fetchAnswers: epFetchAll,
    fetchAnswer: epFetchSingle,
    saveAnswer: async (u, id, payload, _isUpdate) => epSave(u, id, payload),
    deleteAnswer: epRemove,
  };

  // Driver that persists answers via the API-backed file system
  const fileDriver: AnswersDriver = {
    fetchAnswers: fetchAll,
    fetchAnswer: fetchSingle,
    saveAnswer: async (u, id, payload, _isUpdate) => save(u, id, payload),
    deleteAnswer: remove,
  };

  // Default to using the ephemeral driver for the admin portal so that
  // no data is persisted between browser sessions. Only store the driver
  // name in state to avoid serializing functions during SSR.
  const driverType = useState<"ephemeral" | "file">(
    "answersDriverType",
    () => "file"
  );

  const setAnswersDriver = (type: "ephemeral" | "file") => {
    driverType.value = type;
  };

  const getAnswersDriver = (): AnswersDriver =>
    driverType.value === "file" ? fileDriver : ephemeralDriver;

  return { getAnswersDriver, setAnswersDriver, fileDriver, ephemeralDriver };
}
